import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Tooltip,
  CircularProgress,
  Tabs,
  Tab
} from '@mui/material';
import {
  Refresh,
  FileCopy,
  RemoveRedEye,
  Edit,
  Person,
  Send
} from '@mui/icons-material';
import {
  DataItem,
  FormSharedDetails,
  MergedDataItemDetails,
  SelfFilledFormHistory
} from './types';
import { httpRequest } from 'src/services/httpRequest';
import {
  updateBuilding,
  updateChimney,
  updateElectrical,
  updateFire,
  updateMechanical,
  updatePlumbing
} from 'src/redux-store/slices/constructionFormSlice';
import { useDispatch } from 'react-redux';

// Define interface for self-filled form history
interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`form-history-tabpanel-${index}`}
      aria-labelledby={`form-history-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 2 }}>{children}</Box>}
    </div>
  );
}

// 2. Shared Forms History Modal Component
const SharedFormsHistoryModal: React.FC<{
  open: boolean;
  onClose: () => void;
  applicationId: number;
  formTypeId: number;
  formType: string;
}> = ({ open, onClose, applicationId, formTypeId, formType }) => {
  const [shares, setShares] = useState<DataItem[] | []>([]);
  const [selfFilledHistory, setSelfFilledHistory] = useState<DataItem[] | []>(
    []
  );
  const [isLoading, setIsLoading] = useState(false);
  const [isSelfFilledLoading, setIsSelfFilledLoading] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const dispatch = useDispatch();
  const [error, setError] = useState<string | null>(null);
  const [selfFilledError, setSelfFilledError] = useState<string | null>(null);
  const [selectedShare, setSelectedShare] =
    useState<MergedDataItemDetails | null>(null);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const fetchShares = async () => {
    if (!open) return; // Only fetch if modal is open

    setIsLoading(true);
    setError(null);
    setIsSelfFilledLoading(true);
    setSelfFilledError(null);

    try {
      const response = await httpRequest(
        'GET',
        `/construction/ucc_form_share_list?ApplicationID=${applicationId}&UCCFormName=${formType}`
      );
      if (response?.status === 'success') {
        setShares(response?.data?.GuestFilled);
        setSelfFilledHistory(response?.data?.SelfFilled);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load share history');
      setSelfFilledError(
        err.response?.data?.message || 'Failed to load self-filled form history'
      );
    } finally {
      setIsLoading(false);
      setIsSelfFilledLoading(false);
    }
  };

  const fetchSelfFilledHistory = async () => {
    if (!open) return; // Only fetch if modal is open

    setIsSelfFilledLoading(true);
    setSelfFilledError(null);

    try {
      const response = await httpRequest(
        'GET',
        `/construction/ucc_form_self_filled_history?ApplicationID=${applicationId}&UCCFormName=${formType}`
      );
      if (response?.status === 'success') setSelfFilledHistory(response.data);
    } catch (err: any) {
      setSelfFilledError(
        err.response?.data?.message || 'Failed to load self-filled form history'
      );
    } finally {
      setIsSelfFilledLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      fetchShares();
      // fetchSelfFilledHistory();
    }
  }, [open, applicationId, formTypeId]);

  const handleCopyLink = (token: string) => {
    const baseUrl = window.location.origin;
    const formUrl = `${baseUrl}/ucc-form/${token}`;
    navigator.clipboard.writeText(formUrl);
    alert('Link copied to clipboard');
  };

  const handleViewDetails = (
    share: FormSharedDetails,
    UCCSubcodeDetails: any
  ) => {
    setSelectedShare({ ...share, ...UCCSubcodeDetails });
    setDetailsDialogOpen(true);
  };

  const handleViewSelfFilledDetails = (selfFilled: SelfFilledFormHistory) => {
    // eslint-disable-next-line no-console
    console.log('selfFilled', selfFilled);
    // Implement viewing self-filled form details
    // This would depend on your data structure
  };

  const handleSaveFormDetails = (
    share: FormSharedDetails,
    UCCSubcodeDetails: any
  ) => {
    switch (formType) {
      case 'BUILDING':
        dispatch(updateBuilding(UCCSubcodeDetails));
        break;
      case 'ELECTRICAL':
        dispatch(updateElectrical(UCCSubcodeDetails));
        break;
      case 'MECHANICAL':
        dispatch(updateMechanical(UCCSubcodeDetails));
        break;
      case 'PLUMBING':
        dispatch(updatePlumbing(UCCSubcodeDetails));
        break;
      case 'FIRE PROTECTION':
        dispatch(updateFire(UCCSubcodeDetails));
        break;
      case 'CHIMNEY':
        dispatch(updateChimney(UCCSubcodeDetails));
        break;
      default:
    }
    onClose();
  };

  const getStatusChip = (status: string) => {
    switch (status) {
      case 'Pending':
        return <Chip size="small" label="Pending" color="warning" />;
      case 'ACCESSED':
        return <Chip size="small" label="Accessed" color="info" />;
      case 'Completed':
        return <Chip size="small" label="Completed" color="success" />;
      case 'EXPIRED':
        return <Chip size="small" label="Expired" color="error" />;
      default:
        return <Chip size="small" label={status} />;
    }
  };

  // Format date for display
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}
          >
            <Typography variant="h4">{formType} Form History</Typography>
            <Button
              startIcon={<Refresh />}
              size="small"
              onClick={() => {
                fetchShares();
                fetchSelfFilledHistory();
              }}
              disabled={isLoading || isSelfFilledLoading}
            >
              Refresh
            </Button>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            aria-label="form history tabs"
          >
            <Tab
              icon={<Person />}
              iconPosition="start"
              label="Self-Filled"
              id="form-history-tab-0"
            />
            <Tab
              icon={<Send />}
              iconPosition="start"
              label="View Shared Forms"
              id="form-history-tab-1"
            />
          </Tabs>

          {/* Self-Filled Forms History Tab */}
          <TabPanel value={tabValue} index={0}>
            {isSelfFilledLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <CircularProgress />
              </Box>
            ) : selfFilledError ? (
              <Typography color="error">{selfFilledError}</Typography>
            ) : selfFilledHistory === undefined ||
              selfFilledHistory?.length === 0 ? (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ p: 2, textAlign: 'center' }}
              >
                No self-filled form history available
              </Typography>
            ) : (
              <TableContainer
                component={Paper}
                variant="outlined"
                sx={{ mt: 2 }}
              >
                <Table size="medium">
                  <TableHead>
                    <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                      <TableCell>Version</TableCell>
                      <TableCell>Submitted By</TableCell>
                      <TableCell>Date</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {selfFilledHistory?.map((history, index) => (
                      <TableRow key={history.ID || index}>
                        <TableCell>{history.Version || index + 1}</TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {history.SubmittedBy || 'Owner'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {formatDate(history.FormSharedDetails?.CreatedAt)}
                        </TableCell>
                        <TableCell>
                          {getStatusChip(history.UCCSubcodeDetails?.Status)}
                        </TableCell>
                        <TableCell align="right">
                          <Tooltip title="View Details">
                            <IconButton
                              size="small"
                              onClick={() =>
                                handleViewSelfFilledDetails(history)
                              }
                            >
                              <RemoveRedEye fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Edit Details">
                            <IconButton
                              size="small"
                              onClick={() => {
                                handleSaveFormDetails(
                                  history.FormSharedDetails,
                                  history.UCCSubcodeDetails
                                );
                                onClose();
                              }}
                            >
                              <Edit fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </TabPanel>

          {/* Shared Forms History Tab */}
          <TabPanel value={tabValue} index={1}>
            {isLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <CircularProgress />
              </Box>
            ) : error ? (
              <Typography color="error">{error}</Typography>
            ) : shares === undefined || shares?.length === 0 ? (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ p: 2, textAlign: 'center' }}
              >
                No forms have been shared yet
              </Typography>
            ) : (
              <TableContainer
                component={Paper}
                variant="outlined"
                sx={{ mt: 2 }}
              >
                <Table size="medium">
                  <TableHead>
                    <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                      <TableCell>Professional</TableCell>
                      <TableCell>Shared Date</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Expires</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {shares?.map((share) => (
                      <TableRow key={share.FormSharedDetails.ID}>
                        <TableCell>
                          <Typography variant="body2">
                            {share.FormSharedDetails.ProfessionalName}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {share.FormSharedDetails.ProfessionalEmail}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {formatDate(share.FormSharedDetails.CreatedAt)}
                        </TableCell>
                        <TableCell>
                          {Object.keys(share.UCCSubcodeDetails).length === 0
                            ? getStatusChip('Pending')
                            : getStatusChip(share.UCCSubcodeDetails.Status)}
                        </TableCell>
                        <TableCell>
                          {formatDate(share.FormSharedDetails.ExpiryDate)}
                        </TableCell>
                        <TableCell align="right">
                          <Tooltip title="Copy Link">
                            <IconButton
                              size="small"
                              onClick={() =>
                                handleCopyLink(share.FormSharedDetails.UUID)
                              }
                            >
                              <FileCopy fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="View Details">
                            <IconButton
                              size="small"
                              onClick={() =>
                                handleViewDetails(
                                  share.FormSharedDetails,
                                  share.UCCSubcodeDetails
                                )
                              }
                            >
                              <RemoveRedEye fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Edit Details">
                            <IconButton
                              size="small"
                              disabled={
                                Object.keys(share.UCCSubcodeDetails).length ===
                                0
                              }
                              onClick={() =>
                                handleSaveFormDetails(
                                  share.FormSharedDetails,
                                  share.UCCSubcodeDetails
                                )
                              }
                            >
                              <Edit fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </TabPanel>
        </DialogContent>
        <DialogActions>
          <Button variant="contained" color="error" onClick={onClose}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Share Details Dialog */}
      <Dialog
        open={detailsDialogOpen}
        onClose={() => setDetailsDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Share Details</DialogTitle>
        <DialogContent>
          {selectedShare && (
            <Box
              sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}
            >
              <Box>
                <Typography variant="overline" color="text.secondary">
                  Professional
                </Typography>
                <Typography variant="body1">
                  {selectedShare?.ProfessionalName}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {selectedShare.ProfessionalEmail}
                </Typography>
              </Box>

              <Box>
                <Typography variant="overline" color="text.secondary">
                  Form Type
                </Typography>
                <Typography variant="body1">
                  {selectedShare.UCCFormName}
                </Typography>
              </Box>

              <Box>
                <Typography variant="overline" color="text.secondary">
                  Status
                </Typography>
                <Box sx={{ mt: 0.5 }}>
                  {getStatusChip(selectedShare.Status || 'Pending')}
                </Box>
              </Box>

              <Box sx={{ display: 'flex', gap: 4 }}>
                <Box>
                  <Typography variant="overline" color="text.secondary">
                    Shared On
                  </Typography>
                  <Typography variant="body2">
                    {formatDate(selectedShare.CreatedDate)}
                  </Typography>
                </Box>

                <Box>
                  <Typography variant="overline" color="text.secondary">
                    Expires On
                  </Typography>
                  <Typography variant="body2">
                    {formatDate(selectedShare.ExpiryDate)}
                  </Typography>
                </Box>
              </Box>

              <Box>
                <Typography variant="overline" color="text.secondary">
                  Access Link
                </Typography>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    backgroundColor: '#f5f5f5',
                    p: 1,
                    borderRadius: 1
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{ flexGrow: 1, wordBreak: 'break-all' }}
                  >
                    {`${window.location.origin}/ucc-form/${selectedShare.UUID}`}
                  </Typography>
                  <IconButton
                    size="small"
                    onClick={() => handleCopyLink(selectedShare.UUID)}
                  >
                    <FileCopy fontSize="small" />
                  </IconButton>
                </Box>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            variant="contained"
            onClick={() => setDetailsDialogOpen(false)}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default SharedFormsHistoryModal;
